import { css, styled } from 'next-yak';

import { Button } from '@xing-com/button';
import { zIndexElevatedLayer4 } from '@xing-com/layout-tokens';
import { createPolymorphicElement } from '@xing-com/polymorphic-element';
import {
  motionEasingEnter,
  motionEasingLeave,
  motionTimeM,
  scale010,
  scale110,
  spaceL,
  spaceM,
  spaceS,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorError,
  xdlColorErrorSoft,
  xdlColorInfoSoft,
  xdlColorSuccessSoft,
  xdlColorText,
  xdlColorTextOnLight,
} from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import type {
  StatusBannerDisplay,
  StatusBannerVariant,
} from './status-banner.types';

type StatusBannerTProps = {
  $variant: StatusBannerVariant;
  $display: StatusBannerDisplay;
};

type CloseTProps = {
  $variant: StatusBannerVariant;
};

type IconTProps = {
  $variant: StatusBannerVariant;
};

type TextTProps = {
  $variant: StatusBannerVariant;
};

export const statusBannerDuration = motionTimeM;
export const statusBannerTransitionName = 'slide';

// *** Variants ***
export const variants = {
  error: {
    banner: css`
      background-color: ${xdlColorErrorSoft};
    `,
    text: css`
      color: ${xdlColorError};
    `,
    button: css`
      color: ${xdlColorTextOnLight};
    `,
  },
  info: {
    banner: css`
      background-color: ${xdlColorInfoSoft};
    `,
    text: css`
      color: ${xdlColorText};
    `,
    button: css`
      color: ${xdlColorText};
    `,
  },
  success: {
    banner: css`
      background-color: ${xdlColorSuccessSoft};
    `,
    text: css`
      color: ${xdlColorText};
    `,
    button: css`
      color: ${xdlColorText};
    `,
  },
};

// *** Components ***
const StatusBannerElement = createPolymorphicElement('div');

export const StatusBanner = styled(StatusBannerElement)<StatusBannerTProps>`
  box-sizing: border-box;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.15);
  display: flex;
  padding-left: ${spaceM};
  padding-right: ${spaceM};
  width: 100%;

  @media (prefers-contrast: more) {
    border-width: ${scale010};
    border-style: solid;
    border-color: ${xdlColorA11yBorder};
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'error':
        return variants.error.banner;
      case 'info':
        return variants.info.banner;
      case 'success':
        return variants.success.banner;
      default:
        return $variant satisfies undefined;
    }
  }}

  ${({ $display }) => {
    switch ($display) {
      case 'fixed': {
        return css`
          bottom: 0;
          left: 0;
          position: fixed;
          transform: translate3d(0, 100%, 0);
          z-index: ${zIndexElevatedLayer4};

          /* Animation */
          &.${statusBannerTransitionName}-enter {
            transform: translate3d(0, 99.9%, 0);
          }

          &.${statusBannerTransitionName}-enter-active {
            transform: translate3d(0, 0%, 0);
            transition-duration: ${statusBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingEnter};
          }

          &.${statusBannerTransitionName}-enter-done {
            transform: translate3d(0, 0%, 0);
            transition-duration: ${statusBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingEnter};
          }

          &.${statusBannerTransitionName}-exit {
            transform: translate3d(0, 0%, 0);
          }

          &.${statusBannerTransitionName}-exit-active {
            transform: translate3d(0, 99.9%, 0);
            transition-duration: ${statusBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingLeave};
          }

          &.${statusBannerTransitionName}-exit-done {
            transform: translate3d(0, 100%, 0);
            transition-duration: ${statusBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingLeave};
          }
        `;
      }
      case 'inline':
        return undefined;
      default:
        return $display satisfies undefined;
    }
  }}
`;

export const Content = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  padding-bottom: ${spaceL};
  padding-top: ${spaceL};
`;

export const Icon = styled.div<IconTProps>`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: ${scale110};
  justify-content: center;
  width: ${scale110};

  & > svg {
    height: ${scale110};
    width: ${scale110};
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'error':
        return variants.error.text;
      case 'info':
        return variants.info.text;
      case 'success':
        return variants.success.text;
      default:
        return $variant satisfies undefined;
    }
  }}
`;

export const Text = styled(BodyCopy)<TextTProps>`
  && {
    margin-bottom: 0;
    margin-top: ${spaceXXS};
    max-width: inherit;
    padding-left: ${spaceM};

    ${({ $variant }) => {
      switch ($variant) {
        case 'error':
          return variants.error.text;
        case 'info':
          return variants.info.text;
        case 'success':
          return variants.success.text;
        default:
          return $variant satisfies undefined;
      }
    }}
  }
`;

export const Close = styled(Button)<CloseTProps>`
  && {
    align-items: center;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    margin-left: ${spaceM};
    margin-top: ${spaceS};

    ${({ $variant }) => {
      switch ($variant) {
        case 'error':
          return variants.error.button;
        case 'info':
          return variants.info.button;
        case 'success':
          return variants.success.button;
        default:
          return $variant satisfies undefined;
      }
    }}
  }
`;
